import React, { useEffect, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  makeStyles,
} from '@material-ui/core';
import api from '../../services/api';
import { isStandalone } from '../../utils/pwaInstall';
import {
  getPwaIconApiPath,
  normalizePwaIconResponse,
  PWA_CONFIG_VERSION_STORAGE_KEY,
} from '../../constants/pwaBranding';

const useStyles = makeStyles((theme) => ({
  content: {
    paddingTop: theme.spacing(1),
  },
  text: {
    marginBottom: theme.spacing(2),
    color: theme.palette.text.secondary,
  },
}));

/**
 * Quando o app está rodando como PWA (standalone), verifica se a versão do ícone/config
 * no servidor é maior que a última conhecida. Se for, exibe um modal pedindo para a pessoa
 * desinstalar e reinstalar o app para ver as alterações (ícone, nome, etc.).
 * Não é possível forçar desinstalação/reinstalação; apenas avisar.
 */
export default function PwaReinstallModal() {
  const classes = useStyles();
  const [open, setOpen] = useState(false);
  const [checked, setChecked] = useState(false);
  const [serverVersion, setServerVersion] = useState(null);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    if (!isStandalone()) {
      setChecked(true);
      return;
    }
    let cancelled = false;
    api
      .get(getPwaIconApiPath())
      .then((res) => {
        if (cancelled) return;
        const payload = res.data?.data ?? res.data?.result ?? res.data ?? res;
        const { pwaIconVersion } = normalizePwaIconResponse(payload);
        const rawStored = localStorage.getItem(PWA_CONFIG_VERSION_STORAGE_KEY);
        const storedVersion = rawStored !== null ? parseInt(rawStored, 10) : null;
        if (!Number.isFinite(pwaIconVersion)) return;
        if (storedVersion === null) {
          localStorage.setItem(PWA_CONFIG_VERSION_STORAGE_KEY, String(pwaIconVersion));
          return;
        }
        if (pwaIconVersion > storedVersion) {
          setServerVersion(pwaIconVersion);
          setOpen(true);
        }
      })
      .catch(() => {})
      .finally(() => {
        if (!cancelled) setChecked(true);
      });

    return () => {
      cancelled = true;
    };
  }, []);

  const handleClose = () => {
    if (Number.isFinite(serverVersion)) {
      localStorage.setItem(PWA_CONFIG_VERSION_STORAGE_KEY, String(serverVersion));
    }
    setOpen(false);
  };

  if (!isStandalone()) return null;
  if (!checked || !open) return null;

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>Atualização do app</DialogTitle>
      <DialogContent className={classes.content}>
        <Typography className={classes.text}>
          O ícone ou o nome do app foram atualizados. Para ver as alterações no seu celular, é
          preciso remover o app da tela inicial e instalar novamente:
        </Typography>
        <Typography
          variant="body2"
          color="textSecondary"
          component="ol"
          style={{ paddingLeft: 20 }}
        >
          <li>Remova o atalho do app da tela inicial (desinstale o atalho).</li>
          <li>Abra o site no navegador (Chrome, Safari, etc.).</li>
          <li>
            Use a opção &quot;Instalar app&quot; ou &quot;Adicionar à tela inicial&quot; de novo.
          </li>
        </Typography>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} color="primary" variant="contained">
          Entendi
        </Button>
      </DialogActions>
    </Dialog>
  );
}
